import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Textarea } from '../components/ui/textarea';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { Progress } from '../components/ui/progress';
import { toast } from 'sonner';
import { 
  getProjects, createProject, updateProject, deleteProject,
  getTasks, createTask, updateTask, deleteTask, importTasksCSV, importTasksExcel,
  getGrants, createGrant, updateGrant,
  getRisks, createRisk, updateRisk,
  getDashboardProjects
} from '../lib/api';
import { 
  Plus, Search, FolderKanban, CheckCircle2, Clock, AlertTriangle,
  Target, Users, Calendar, TrendingUp, Wallet, MoreVertical, Upload, FileUp
} from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../components/ui/dropdown-menu';

const PROJECT_STATUSES = ['planning', 'in_progress', 'on_hold', 'completed', 'cancelled'];
const TASK_STATUSES = ['todo', 'in_progress', 'awaiting_feedback', 'done'];
const TASK_PRIORITIES = ['low', 'medium', 'high', 'urgent'];
const RISK_STATUSES = ['open', 'mitigated', 'closed'];

const statusColors = {
  planning: 'bg-blue-100 text-blue-700',
  in_progress: 'bg-amber-100 text-amber-700',
  on_hold: 'bg-slate-100 text-slate-700',
  completed: 'bg-emerald-100 text-emerald-700',
  cancelled: 'bg-rose-100 text-rose-700',
  todo: 'bg-slate-100 text-slate-700',
  awaiting_feedback: 'bg-purple-100 text-purple-700',
  done: 'bg-emerald-100 text-emerald-700',
  active: 'bg-emerald-100 text-emerald-700',
  closed: 'bg-slate-100 text-slate-700',
  open: 'bg-amber-100 text-amber-700',
  mitigated: 'bg-blue-100 text-blue-700'
};

const priorityColors = {
  low: 'bg-slate-100 text-slate-700',
  medium: 'bg-blue-100 text-blue-700',
  high: 'bg-amber-100 text-amber-700',
  urgent: 'bg-rose-100 text-rose-700'
};

const formatCurrency = (amount) => {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 0 }).format(amount);
};

// Project Form Component
const ProjectForm = ({ project, onSave, onClose }) => {
  const [form, setForm] = useState(project || {
    name: '', description: '', client_name: '', budget: 0, 
    start_date: '', end_date: '', status: 'planning'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (project?.id) {
        await updateProject(project.id, form);
        toast.success('Project updated');
      } else {
        await createProject(form);
        toast.success('Project created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save project');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2 col-span-2">
          <Label>Project Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="project-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Client</Label>
          <Input value={form.client_name} onChange={(e) => setForm({ ...form, client_name: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Budget (ZAR)</Label>
          <Input type="number" min="0" value={form.budget} onChange={(e) => setForm({ ...form, budget: parseFloat(e.target.value) || 0 })} />
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Start Date</Label>
          <Input type="date" value={form.start_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>End Date</Label>
          <Input type="date" value={form.end_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {PROJECT_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace('_', ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-project-btn">{saving ? 'Saving...' : 'Save Project'}</Button>
      </div>
    </form>
  );
};

// Task Form Component
const TaskForm = ({ task, projects, onSave, onClose }) => {
  const [form, setForm] = useState(task || {
    project_id: '', name: '', description: '', start_date: '', due_date: '', 
    priority: 'medium', status: 'todo', team_members: [], reminder_emails: []
  });
  const [saving, setSaving] = useState(false);
  const [teamMemberInput, setTeamMemberInput] = useState('');
  const [reminderEmailInput, setReminderEmailInput] = useState('');

  const addTeamMember = () => {
    if (teamMemberInput.trim() && !form.team_members.includes(teamMemberInput.trim())) {
      setForm({ ...form, team_members: [...form.team_members, teamMemberInput.trim()] });
      setTeamMemberInput('');
    }
  };

  const removeTeamMember = (member) => {
    setForm({ ...form, team_members: form.team_members.filter(m => m !== member) });
  };

  const addReminderEmail = () => {
    if (reminderEmailInput.trim() && !form.reminder_emails.includes(reminderEmailInput.trim())) {
      setForm({ ...form, reminder_emails: [...form.reminder_emails, reminderEmailInput.trim()] });
      setReminderEmailInput('');
    }
  };

  const removeReminderEmail = (email) => {
    setForm({ ...form, reminder_emails: form.reminder_emails.filter(e => e !== email) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Set assigned_to from first team member if not set
      const submitData = {
        ...form,
        assigned_to: form.team_members[0] || form.assigned_to || null
      };
      
      if (task?.id) {
        await updateTask(task.id, submitData);
        toast.success('Task updated');
      } else {
        await createTask(submitData);
        toast.success('Task created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save task');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Project (Optional)</Label>
          <Select value={form.project_id || 'none'} onValueChange={(v) => setForm({ ...form, project_id: v === 'none' ? null : v })}>
            <SelectTrigger data-testid="task-project-select" className="h-9"><SelectValue placeholder="Select project" /></SelectTrigger>
            <SelectContent>
              <SelectItem value="none">No Project</SelectItem>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Task Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="task-name-input" className="h-9" />
        </div>
      </div>
      <div className="grid grid-cols-4 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Start Date</Label>
          <Input type="date" value={form.start_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} className="h-9" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Due Date</Label>
          <Input type="date" value={form.due_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, due_date: e.target.value })} className="h-9" />
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Priority</Label>
          <Select value={form.priority} onValueChange={(v) => setForm({ ...form, priority: v })}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TASK_PRIORITIES.map(p => <SelectItem key={p} value={p} className="capitalize">{p}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger className="h-9"><SelectValue /></SelectTrigger>
            <SelectContent>
              {TASK_STATUSES.map(s => <SelectItem key={s} value={s}>{s.replace(/_/g, ' ')}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      
      {/* Team Members & Reminder Emails - side by side */}
      <div className="grid grid-cols-2 gap-3">
        <div className="space-y-1">
          <Label className="text-xs">Team Members</Label>
          <div className="flex gap-1">
            <Input 
              value={teamMemberInput} 
              onChange={(e) => setTeamMemberInput(e.target.value)} 
              placeholder="Add member"
              className="h-8 text-sm"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addTeamMember())}
            />
            <Button type="button" variant="outline" size="sm" onClick={addTeamMember} className="h-8 px-2">+</Button>
          </div>
          {form.team_members?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {form.team_members.map((member, idx) => (
                <Badge key={idx} variant="secondary" className="px-1.5 py-0.5 text-xs">
                  {member}
                  <button type="button" onClick={() => removeTeamMember(member)} className="ml-1 text-slate-500 hover:text-slate-700">&times;</button>
                </Badge>
              ))}
            </div>
          )}
        </div>
        <div className="space-y-1">
          <Label className="text-xs">Reminder Emails</Label>
          <div className="flex gap-1">
            <Input 
              type="email"
              value={reminderEmailInput} 
              onChange={(e) => setReminderEmailInput(e.target.value)} 
              placeholder="Add email"
              className="h-8 text-sm"
              onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addReminderEmail())}
            />
            <Button type="button" variant="outline" size="sm" onClick={addReminderEmail} className="h-8 px-2">+</Button>
          </div>
          {form.reminder_emails?.length > 0 && (
            <div className="flex flex-wrap gap-1 mt-1">
              {form.reminder_emails.map((email, idx) => (
                <Badge key={idx} variant="outline" className="px-1.5 py-0.5 text-xs">
                  {email}
                  <button type="button" onClick={() => removeReminderEmail(email)} className="ml-1 text-slate-500 hover:text-slate-700">&times;</button>
                </Badge>
              ))}
            </div>
          )}
        </div>
      </div>

      <div className="space-y-1">
        <Label className="text-xs">Description</Label>
        <Textarea value={form.description || ''} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} className="text-sm" />
      </div>
      <div className="flex justify-end gap-2 pt-2">
        <Button type="button" variant="outline" size="sm" onClick={onClose}>Cancel</Button>
        <Button type="submit" size="sm" disabled={saving} data-testid="save-task-btn">{saving ? 'Saving...' : 'Save Task'}</Button>
      </div>
    </form>
  );
};

// Grant Form Component
const GrantForm = ({ grant, projects, onSave, onClose }) => {
  const [form, setForm] = useState(grant || {
    name: '', donor: '', amount: 0, start_date: '', end_date: '', 
    project_id: '', conditions: '', status: 'active'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (grant?.id) {
        await updateGrant(grant.id, form);
        toast.success('Grant updated');
      } else {
        await createGrant(form);
        toast.success('Grant created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save grant');
    } finally {
      setSaving(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Grant Name *</Label>
          <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="grant-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Donor *</Label>
          <Input value={form.donor} onChange={(e) => setForm({ ...form, donor: e.target.value })} required />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label>Amount (ZAR)</Label>
          <Input type="number" min="0" value={form.amount} onChange={(e) => setForm({ ...form, amount: parseFloat(e.target.value) || 0 })} />
        </div>
        <div className="space-y-2">
          <Label>Linked Project</Label>
          <Select value={form.project_id || ''} onValueChange={(v) => setForm({ ...form, project_id: v })}>
            <SelectTrigger><SelectValue placeholder="Select project (optional)" /></SelectTrigger>
            <SelectContent>
              {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Start Date</Label>
          <Input type="date" value={form.start_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, start_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>End Date</Label>
          <Input type="date" value={form.end_date?.split('T')[0] || ''} onChange={(e) => setForm({ ...form, end_date: e.target.value })} />
        </div>
        <div className="space-y-2">
          <Label>Status</Label>
          <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="active">Active</SelectItem>
              <SelectItem value="completed">Completed</SelectItem>
              <SelectItem value="suspended">Suspended</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Conditions</Label>
        <Textarea value={form.conditions} onChange={(e) => setForm({ ...form, conditions: e.target.value })} rows={3} placeholder="Grant conditions and requirements" />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving} data-testid="save-grant-btn">{saving ? 'Saving...' : 'Save Grant'}</Button>
      </div>
    </form>
  );
};

// Risk Form Component
const RiskForm = ({ risk, projects, onSave, onClose }) => {
  const [form, setForm] = useState(risk || {
    project_id: '', title: '', description: '', probability: 3, impact: 3, mitigation: '', status: 'open'
  });
  const [saving, setSaving] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (risk?.id) {
        await updateRisk(risk.id, form);
        toast.success('Risk updated');
      } else {
        await createRisk(form);
        toast.success('Risk created');
      }
      onSave();
      onClose();
    } catch (error) {
      toast.error('Failed to save risk');
    } finally {
      setSaving(false);
    }
  };

  const riskScore = form.probability * form.impact;
  const riskLevel = riskScore >= 15 ? 'High' : riskScore >= 8 ? 'Medium' : 'Low';
  const riskColor = riskScore >= 15 ? 'text-rose-600' : riskScore >= 8 ? 'text-amber-600' : 'text-emerald-600';

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-2">
        <Label>Project *</Label>
        <Select value={form.project_id} onValueChange={(v) => setForm({ ...form, project_id: v })}>
          <SelectTrigger data-testid="risk-project-select"><SelectValue placeholder="Select project" /></SelectTrigger>
          <SelectContent>
            {projects.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Risk Title *</Label>
        <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} required data-testid="risk-title-input" />
      </div>
      <div className="grid grid-cols-3 gap-4">
        <div className="space-y-2">
          <Label>Probability (1-5)</Label>
          <Select value={String(form.probability)} onValueChange={(v) => setForm({ ...form, probability: parseInt(v) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Impact (1-5)</Label>
          <Select value={String(form.impact)} onValueChange={(v) => setForm({ ...form, impact: parseInt(v) })}>
            <SelectTrigger><SelectValue /></SelectTrigger>
            <SelectContent>
              {[1, 2, 3, 4, 5].map(n => <SelectItem key={n} value={String(n)}>{n}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-2">
          <Label>Risk Score</Label>
          <div className={`h-10 flex items-center justify-center font-bold ${riskColor} bg-muted rounded-md`}>
            {riskScore} ({riskLevel})
          </div>
        </div>
      </div>
      <div className="space-y-2">
        <Label>Status</Label>
        <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
          <SelectTrigger><SelectValue /></SelectTrigger>
          <SelectContent>
            {RISK_STATUSES.map(s => <SelectItem key={s} value={s}>{s}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>
      <div className="space-y-2">
        <Label>Description</Label>
        <Textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={2} />
      </div>
      <div className="space-y-2">
        <Label>Mitigation Strategy</Label>
        <Textarea value={form.mitigation} onChange={(e) => setForm({ ...form, mitigation: e.target.value })} rows={2} />
      </div>
      <div className="flex justify-end gap-2 pt-4">
        <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
        <Button type="submit" disabled={saving || !form.project_id} data-testid="save-risk-btn">{saving ? 'Saving...' : 'Save Risk'}</Button>
      </div>
    </form>
  );
};

// Risk Heat Map Component
const RiskHeatMap = ({ risks }) => {
  const grid = Array(5).fill(null).map(() => Array(5).fill([]));
  
  risks.forEach(risk => {
    const prob = Math.min(5, Math.max(1, risk.probability)) - 1;
    const imp = Math.min(5, Math.max(1, risk.impact)) - 1;
    grid[4 - imp][prob] = [...(grid[4 - imp][prob] || []), risk];
  });

  const getCellColor = (prob, imp) => {
    const score = (prob + 1) * (5 - imp);
    if (score >= 15) return 'bg-rose-500';
    if (score >= 8) return 'bg-amber-500';
    return 'bg-emerald-500';
  };

  return (
    <div className="space-y-2">
      <div className="flex">
        <div className="w-8 flex flex-col justify-between text-xs text-muted-foreground py-1 pr-2">
          <span>5</span>
          <span>4</span>
          <span>3</span>
          <span>2</span>
          <span>1</span>
        </div>
        <div className="flex-1 grid grid-cols-5 gap-1">
          {grid.map((row, rowIdx) =>
            row.map((cell, colIdx) => (
              <div
                key={`${rowIdx}-${colIdx}`}
                className={`h-12 rounded flex items-center justify-center text-white font-bold text-sm ${getCellColor(colIdx, rowIdx)} ${cell.length > 0 ? 'opacity-100' : 'opacity-30'}`}
              >
                {cell.length > 0 && cell.length}
              </div>
            ))
          )}
        </div>
      </div>
      <div className="flex justify-between text-xs text-muted-foreground px-8">
        <span>1</span>
        <span>2</span>
        <span>3</span>
        <span>4</span>
        <span>5</span>
      </div>
      <div className="text-center text-xs text-muted-foreground">Probability</div>
    </div>
  );
};

// Main Projects Page Component
export const ProjectsPage = () => {
  const [projects, setProjects] = useState([]);
  const [tasks, setTasks] = useState([]);
  const [grants, setGrants] = useState([]);
  const [risks, setRisks] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  const [projectDialogOpen, setProjectDialogOpen] = useState(false);
  const [taskDialogOpen, setTaskDialogOpen] = useState(false);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [riskDialogOpen, setRiskDialogOpen] = useState(false);
  
  const [selectedProject, setSelectedProject] = useState(null);
  const [selectedTask, setSelectedTask] = useState(null);
  const [selectedGrant, setSelectedGrant] = useState(null);
  const [selectedRisk, setSelectedRisk] = useState(null);
  const [taskImportDialogOpen, setTaskImportDialogOpen] = useState(false);
  const [importing, setImporting] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [projRes, taskRes, grantRes, riskRes, dashRes] = await Promise.all([
        getProjects(), getTasks(), getGrants(), getRisks(), getDashboardProjects()
      ]);
      setProjects(projRes.data);
      setTasks(taskRes.data);
      setGrants(grantRes.data);
      setRisks(riskRes.data);
      setDashboard(dashRes.data);
    } catch (error) {
      console.error('Failed to load data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProject = async (id) => {
    try {
      await deleteProject(id);
      toast.success('Project deleted');
      loadData();
    } catch (error) {
      toast.error('Failed to delete project');
    }
  };

  const handleTaskCSVImport = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const isCSV = file.name.endsWith('.csv');
    const isExcel = file.name.endsWith('.xlsx') || file.name.endsWith('.xls');

    if (!isCSV && !isExcel) {
      toast.error('Please select a CSV or Excel (.xlsx) file');
      return;
    }

    setImporting(true);
    try {
      let response;
      
      if (isCSV) {
        // Handle CSV file
        const text = await file.text();
        response = await importTasksCSV(text, null);
      } else {
        // Handle Excel file - convert to base64
        const arrayBuffer = await file.arrayBuffer();
        const base64 = btoa(
          new Uint8Array(arrayBuffer).reduce((data, byte) => data + String.fromCharCode(byte), '')
        );
        response = await importTasksExcel(base64, null);
      }
      
      const { imported_count, failed_count, errors } = response.data;
      
      if (imported_count > 0) {
        toast.success(`Successfully imported ${imported_count} tasks`);
      }
      if (failed_count > 0) {
        toast.warning(`${failed_count} rows failed to import`);
        if (errors?.length > 0) {
          console.log('Import errors:', errors);
        }
      }
      
      loadData();
      setTaskImportDialogOpen(false);
    } catch (error) {
      console.error('Import error:', error);
      toast.error('Failed to import file');
    } finally {
      setImporting(false);
      e.target.value = '';
    }
  };

  const filteredProjects = projects.filter(p => 
    p.name.toLowerCase().includes(search.toLowerCase()) ||
    p.client_name?.toLowerCase().includes(search.toLowerCase())
  );

  if (loading) {
    return <div className="flex items-center justify-center h-96"><div className="animate-pulse">Loading...</div></div>;
  }

  const highRiskCount = risks.filter(r => r.risk_score >= 15).length;

  return (
    <div className="space-y-6" data-testid="projects-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">Projects & Grants</h1>
          <p className="text-muted-foreground mt-1">Manage projects, tasks, grants and risks</p>
        </div>
      </div>

      {/* KPI Cards */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-blue-100 flex items-center justify-center">
                <FolderKanban className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Total Projects</p>
                <p className="text-2xl font-bold font-mono">{dashboard?.total_projects || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-amber-100 flex items-center justify-center">
                <Wallet className="h-6 w-6 text-amber-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Budget Utilization</p>
                <p className="text-2xl font-bold font-mono">{dashboard?.budget_utilization || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-emerald-100 flex items-center justify-center">
                <CheckCircle2 className="h-6 w-6 text-emerald-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">Tasks Completed</p>
                <p className="text-2xl font-bold font-mono">{dashboard?.task_status?.done || 0} / {dashboard?.total_tasks || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="kpi-card">
          <CardContent className="p-6">
            <div className="flex items-center gap-4">
              <div className="h-12 w-12 rounded-lg bg-rose-100 flex items-center justify-center">
                <AlertTriangle className="h-6 w-6 text-rose-600" />
              </div>
              <div>
                <p className="text-sm text-muted-foreground">High Risks</p>
                <p className="text-2xl font-bold font-mono text-rose-600">{highRiskCount}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      <Tabs defaultValue="projects" className="space-y-6">
        <div className="flex items-center justify-between">
          <TabsList>
            <TabsTrigger value="projects" data-testid="projects-tab">Projects ({projects.length})</TabsTrigger>
            <TabsTrigger value="tasks" data-testid="tasks-tab">Tasks ({tasks.length})</TabsTrigger>
            <TabsTrigger value="grants" data-testid="grants-tab">Grants ({grants.length})</TabsTrigger>
            <TabsTrigger value="risks" data-testid="risks-tab">Risks ({risks.length})</TabsTrigger>
          </TabsList>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input placeholder="Search..." className="pl-9 w-64" value={search} onChange={(e) => setSearch(e.target.value)} />
          </div>
        </div>

        {/* Projects Tab */}
        <TabsContent value="projects" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={projectDialogOpen} onOpenChange={setProjectDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedProject(null)} data-testid="add-project-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Project
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedProject ? 'Edit Project' : 'New Project'}</DialogTitle>
                </DialogHeader>
                <ProjectForm project={selectedProject} onSave={loadData} onClose={() => setProjectDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {filteredProjects.map((project) => (
              <Card key={project.id} className="hover:shadow-md transition-shadow" data-testid={`project-card-${project.id}`}>
                <CardHeader className="pb-2">
                  <div className="flex items-start justify-between">
                    <div>
                      <CardTitle className="text-lg">{project.name}</CardTitle>
                      <p className="text-sm text-muted-foreground">{project.client_name || 'No client'}</p>
                    </div>
                    <DropdownMenu>
                      <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm"><MoreVertical className="h-4 w-4" /></Button>
                      </DropdownMenuTrigger>
                      <DropdownMenuContent align="end">
                        <DropdownMenuItem onClick={() => { setSelectedProject(project); setProjectDialogOpen(true); }}>Edit</DropdownMenuItem>
                        <DropdownMenuItem className="text-rose-600" onClick={() => handleDeleteProject(project.id)}>Delete</DropdownMenuItem>
                      </DropdownMenuContent>
                    </DropdownMenu>
                  </div>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center justify-between">
                    <Badge className={statusColors[project.status]}>{project.status.replace('_', ' ')}</Badge>
                    <span className="font-mono text-sm font-semibold text-amber-600">{formatCurrency(project.budget)}</span>
                  </div>
                  <div className="space-y-2">
                    <div className="flex justify-between text-sm">
                      <span className="text-muted-foreground">Progress</span>
                      <span className="font-mono">{project.completion_percentage || 0}%</span>
                    </div>
                    <Progress value={project.completion_percentage || 0} className="h-2" />
                  </div>
                  <div className="flex items-center gap-4 text-sm text-muted-foreground">
                    <div className="flex items-center gap-1">
                      <Calendar className="h-4 w-4" />
                      {project.end_date?.split('T')[0] || 'No deadline'}
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </TabsContent>

        {/* Tasks Tab */}
        <TabsContent value="tasks" className="space-y-4">
          <div className="flex justify-end gap-2">
            {/* CSV/Excel Import Dialog */}
            <Dialog open={taskImportDialogOpen} onOpenChange={setTaskImportDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="import-tasks-btn">
                  <Upload className="h-4 w-4 mr-2" /> Import Tasks
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-md">
                <DialogHeader>
                  <DialogTitle>Import Tasks from File</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <p className="text-sm text-muted-foreground">
                    Upload a CSV or Excel (.xlsx) file containing tasks. Expected columns: Tasks #, Name, Status, Start Date, Due Date, Assigned to, Tags, Priority
                  </p>
                  <div className="border-2 border-dashed border-slate-200 rounded-lg p-6 text-center">
                    <FileUp className="h-10 w-10 mx-auto text-muted-foreground mb-2" />
                    <label className="cursor-pointer">
                      <span className="text-primary font-medium hover:underline">
                        {importing ? 'Importing...' : 'Choose CSV or Excel file'}
                      </span>
                      <input
                        type="file"
                        accept=".csv,.xlsx,.xls"
                        className="hidden"
                        onChange={handleTaskCSVImport}
                        disabled={importing}
                        data-testid="task-file-input"
                      />
                    </label>
                    <p className="text-xs text-muted-foreground mt-2">
                      Tasks will be linked to your account. Team members from "Assigned to" column (comma-separated).
                    </p>
                  </div>
                </div>
              </DialogContent>
            </Dialog>

            {/* Add Task Dialog */}
            <Dialog open={taskDialogOpen} onOpenChange={setTaskDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedTask(null)} data-testid="add-task-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Task
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle className="text-base">{selectedTask ? 'Edit Task' : 'New Task'}</DialogTitle>
                </DialogHeader>
                <TaskForm task={selectedTask} projects={projects} onSave={loadData} onClose={() => setTaskDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>#</TableHead>
                  <TableHead>Task</TableHead>
                  <TableHead>Owner</TableHead>
                  <TableHead>Team Members</TableHead>
                  <TableHead>Start Date</TableHead>
                  <TableHead>Due Date</TableHead>
                  <TableHead>Priority</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {tasks.map((task) => {
                  return (
                    <TableRow key={task.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedTask(task); setTaskDialogOpen(true); }}>
                      <TableCell className="text-muted-foreground font-mono text-sm">{task.task_number || '-'}</TableCell>
                      <TableCell className="font-medium max-w-md truncate">{task.name}</TableCell>
                      <TableCell className="text-sm">
                        {task.owner_email ? (
                          <span className="text-primary" title={task.owner_email}>
                            {task.owner_email.split('@')[0]}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">-</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex flex-wrap gap-1 max-w-xs">
                          {task.team_members?.slice(0, 2).map((member, idx) => (
                            <Badge key={idx} variant="outline" className="text-xs">{member}</Badge>
                          ))}
                          {task.team_members?.length > 2 && (
                            <Badge variant="secondary" className="text-xs">+{task.team_members.length - 2}</Badge>
                          )}
                          {(!task.team_members || task.team_members.length === 0) && (
                            <span className="text-muted-foreground text-sm">-</span>
                          )}
                        </div>
                      </TableCell>
                      <TableCell className="text-sm">{task.start_date?.split('T')[0] || '-'}</TableCell>
                      <TableCell className="text-sm">{task.due_date?.split('T')[0] || '-'}</TableCell>
                      <TableCell><Badge className={priorityColors[task.priority]}>{task.priority}</Badge></TableCell>
                      <TableCell><Badge className={statusColors[task.status]}>{task.status?.replace(/_/g, ' ') || 'todo'}</Badge></TableCell>
                    </TableRow>
                  );
                })}
                {tasks.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">No tasks found. Import from CSV/Excel or create a new task.</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Grants Tab */}
        <TabsContent value="grants" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={grantDialogOpen} onOpenChange={setGrantDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedGrant(null)} data-testid="add-grant-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Grant
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>{selectedGrant ? 'Edit Grant' : 'New Grant'}</DialogTitle>
                </DialogHeader>
                <GrantForm grant={selectedGrant} projects={projects} onSave={loadData} onClose={() => setGrantDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <Card>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Grant Name</TableHead>
                  <TableHead>Donor</TableHead>
                  <TableHead className="text-right">Amount</TableHead>
                  <TableHead className="text-right">Disbursed</TableHead>
                  <TableHead>Compliance</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {grants.map((grant) => (
                  <TableRow key={grant.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedGrant(grant); setGrantDialogOpen(true); }}>
                    <TableCell className="font-medium">{grant.name}</TableCell>
                    <TableCell>{grant.donor}</TableCell>
                    <TableCell className="text-right font-mono">{formatCurrency(grant.amount)}</TableCell>
                    <TableCell className="text-right font-mono text-amber-600">{formatCurrency(grant.disbursed)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Progress value={grant.compliance_score} className="h-2 w-16" />
                        <span className="text-sm font-mono">{grant.compliance_score}%</span>
                      </div>
                    </TableCell>
                    <TableCell><Badge className={statusColors[grant.status]}>{grant.status}</Badge></TableCell>
                  </TableRow>
                ))}
                {grants.length === 0 && (
                  <TableRow>
                    <TableCell colSpan={6} className="text-center py-8 text-muted-foreground">No grants found</TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </Card>
        </TabsContent>

        {/* Risks Tab */}
        <TabsContent value="risks" className="space-y-4">
          <div className="flex justify-end">
            <Dialog open={riskDialogOpen} onOpenChange={setRiskDialogOpen}>
              <DialogTrigger asChild>
                <Button onClick={() => setSelectedRisk(null)} data-testid="add-risk-btn">
                  <Plus className="h-4 w-4 mr-2" /> Add Risk
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-xl">
                <DialogHeader>
                  <DialogTitle>{selectedRisk ? 'Edit Risk' : 'New Risk'}</DialogTitle>
                </DialogHeader>
                <RiskForm risk={selectedRisk} projects={projects} onSave={loadData} onClose={() => setRiskDialogOpen(false)} />
              </DialogContent>
            </Dialog>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            <Card className="lg:col-span-2">
              <CardHeader>
                <CardTitle className="text-lg">Risk Register</CardTitle>
              </CardHeader>
              <CardContent className="p-0">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Risk</TableHead>
                      <TableHead>Project</TableHead>
                      <TableHead className="text-center">Score</TableHead>
                      <TableHead>Status</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {risks.map((risk) => {
                      const project = projects.find(p => p.id === risk.project_id);
                      const scoreColor = risk.risk_score >= 15 ? 'text-rose-600 bg-rose-100' : risk.risk_score >= 8 ? 'text-amber-600 bg-amber-100' : 'text-emerald-600 bg-emerald-100';
                      return (
                        <TableRow key={risk.id} className="cursor-pointer hover:bg-muted/50" onClick={() => { setSelectedRisk(risk); setRiskDialogOpen(true); }}>
                          <TableCell className="font-medium">{risk.title}</TableCell>
                          <TableCell className="text-muted-foreground">{project?.name || '-'}</TableCell>
                          <TableCell className="text-center">
                            <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full font-mono font-bold text-sm ${scoreColor}`}>
                              {risk.risk_score}
                            </span>
                          </TableCell>
                          <TableCell><Badge className={statusColors[risk.status]}>{risk.status}</Badge></TableCell>
                        </TableRow>
                      );
                    })}
                    {risks.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center py-8 text-muted-foreground">No risks found</TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <CardTitle className="text-lg">Risk Heat Map</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="text-xs text-muted-foreground mb-2 text-center">Impact</div>
                <RiskHeatMap risks={risks} />
                <div className="flex justify-center gap-4 mt-4 text-xs">
                  <div className="flex items-center gap-1">
                    <div className="h-3 w-3 rounded bg-emerald-500"></div> Low
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="h-3 w-3 rounded bg-amber-500"></div> Medium
                  </div>
                  <div className="flex items-center gap-1">
                    <div className="h-3 w-3 rounded bg-rose-500"></div> High
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>
      </Tabs>
    </div>
  );
};
